import React from 'react';
import styled from 'styled-components';
import errorReporter from '@app/errorReporter';
import {
  Col,
  Row,
  Icon,
  Button,
  Text,
  Dropdown,
  Menu,
  MenuItem,
} from 'components';
import { useBreakpoint, useDebounce } from 'hooks';
import { AuthContext, LayoutContext } from 'contexts';
import {
  BrokerBayAdminController,
  ListingController,
  UserController,
} from 'api';
import {
  isBrokerBayAdminEmail,
  assumeBarMobileAssumedHeight,
  assumeBarMobileHeight,
  DASHBOARD_LISTING_STORAGE_KEYS,
} from 'utils';
import { AssumeUser } from './types';
import MlsNumberSelectionModal from './MlsNumberSelectionModal';

// also used to prevent body scroll in TourEditPage/hooks/useFixMobile
export const BB_ASSUME_INPUT_CLASS = 'bb-assume-nav-input';

const AssumeByOptions: Array<string> = [
  'User Email',
  'ORG ID',
  'Listing ID',
  'User ID',
];

type AssumeByOptionsType = (typeof AssumeByOptions)[number];

const BBAssumeNavbar: React.FC<{
  anchorRef: React.MutableRefObject<HTMLDivElement>;
}> = ({ anchorRef }) => {
  const { auth } = React.useContext(AuthContext);
  const { updateTopElementHeight: updateHeight } =
    React.useContext(LayoutContext);
  const { md } = useBreakpoint();

  const [assumeUser, setAssumeUser] = React.useState<AssumeUser[]>([]);
  const [inputValue, setInputValue] = React.useState('');
  const [mlsModalOpen, setMlsModalOpen] = React.useState(false);
  const debouncedValue = useDebounce(inputValue, 500);

  const [assumeBySelection, setAssumeBySelection] =
    React.useState<AssumeByOptionsType>(auth.assumeBySelection || 'User Email');
  const [overlayVisible, setOverlayVisible] = React.useState(false);

  const fetchByUserEmail = async () => {
    if (
      debouncedValue.indexOf('@') === -1 ||
      debouncedValue.indexOf('.') === -1
    ) {
      setAssumeUser([]);
      return;
    }
    try {
      const users = await BrokerBayAdminController.getUserIdByEmail(
        debouncedValue.toLowerCase().trim(),
      );
      if (users.length) {
        setAssumeUser(users);
      }
    } catch (err) {
      errorReporter.error(err);
    }
  };

  const fetchByOrgId = async (id?: string) => {
    const orgId = id || debouncedValue;
    if (orgId.length < 24) return;
    try {
      const data = await UserController.listOrgAdmins(orgId.trim());
      const { id: globalAdminId } =
        data.admins?.find(admin => isBrokerBayAdminEmail(admin.email)) || {};
      if (globalAdminId) {
        setAssumeUser([{ id: globalAdminId }]);
      }
    } catch (err) {
      setAssumeUser([]);
      errorReporter.error(err);
    }
  };

  const fetchByListingId = async () => {
    if (debouncedValue.length < 24) return;
    try {
      const { org } = await ListingController.getListing(debouncedValue.trim());
      await fetchByOrgId(org.id);
    } catch (err) {
      setAssumeUser([]);
      errorReporter.error(err);
    }
  };

  const fetchByUserId = async () => {
    try {
      const { exists } =
        await BrokerBayAdminController.checkExistingUserById(debouncedValue);
      if (exists) {
        setAssumeUser([{ id: debouncedValue.trim() }]);
      } else {
        setAssumeUser([]);
      }
    } catch (err) {
      errorReporter.error(err);
    }
  };

  const fetch = () => {
    if (assumeBySelection === 'User Email') fetchByUserEmail();
    if (assumeBySelection === 'ORG ID') fetchByOrgId();
    if (assumeBySelection === 'Listing ID') fetchByListingId();
    if (assumeBySelection === 'User ID') fetchByUserId();
  };

  const handleChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(value);
  };

  const handleAssumeByOptionChange = (option: AssumeByOptionsType) => {
    setAssumeBySelection(option);
    setOverlayVisible(false);
  };

  const hide = () => {
    updateHeight('bbAssume', 0);
  };

  const onAssume = () => {
    if (!assumeUser.length) return;

    if (assumeUser.length === 1) {
      const assumeId = assumeUser[0].id;

      // Clear dashboard listing storage keys on assumption
      localStorage.removeItem(DASHBOARD_LISTING_STORAGE_KEYS.status);
      localStorage.removeItem(DASHBOARD_LISTING_STORAGE_KEYS.availability);

      window.location.href = `/brokerbay_admin/assume/${assumeId}?listingId=${
        assumeBySelection === 'Listing ID' ? debouncedValue.trim() : ''
      }&assumeBySelection=${assumeBySelection}`;
    } else {
      setMlsModalOpen(true);
    }
  };

  React.useEffect(() => {
    if (!md) setAssumeBySelection('User Email');
    if (!md && auth.assumingUserId) {
      updateHeight('bbAssume', assumeBarMobileAssumedHeight);
    } else {
      updateHeight('bbAssume', assumeBarMobileHeight);
    }
  }, [auth, md, updateHeight]);

  React.useEffect(() => {
    if (debouncedValue) {
      fetch();
    } else {
      setAssumeUser([]);
    }
  }, [debouncedValue]);

  React.useEffect(() => {
    if (assumeBySelection && debouncedValue) fetch();
  }, [assumeBySelection]);

  return (
    <>
      <Row align="middle" className="assume-row">
        {md && (
          <Col className="dropdown-column">
            <Dropdown
              dropdownRender={() => (
                <StyledMenu>
                  {AssumeByOptions.map(option => (
                    <StyledMenuItem
                      key={option}
                      onClick={() => handleAssumeByOptionChange(option)}
                      $isSelected={assumeBySelection === option}
                    >
                      <StyledMenuText>{option}</StyledMenuText>
                    </StyledMenuItem>
                  ))}
                </StyledMenu>
              )}
              trigger={['click']}
              placement="bottomLeft"
              open={overlayVisible}
              onOpenChange={visible => setOverlayVisible(visible)}
              getPopupContainer={() => anchorRef.current}
            >
              <div>
                <StyledDropdownText>
                  Assume by {assumeBySelection}
                  <StyledArrowIcon
                    $overlayVisible={overlayVisible}
                    type="arrowDownThin"
                    width={9}
                    color="white"
                  />
                </StyledDropdownText>
              </div>
            </Dropdown>
          </Col>
        )}
        <Col>
          <Icon type="search" width={15} color="white" />
        </Col>
        <Col flex={!md ? 'auto' : ''}>
          <input
            type="text"
            value={inputValue}
            onChange={handleChange}
            placeholder={
              assumeBySelection === 'User Email' ? 'Email' : assumeBySelection
            }
            className={BB_ASSUME_INPUT_CLASS}
          />
        </Col>
        {assumeUser.length > 0 && (
          <Col>
            <Button
              type="primary"
              danger
              className="assume-button"
              onClick={onAssume}
            >
              <Text>Assume</Text>
            </Button>
          </Col>
        )}
        {auth.assumingUserId && md && (
          <Col>
            <UnassumeButton />
          </Col>
        )}
        <Col flex={!md ? '' : 'auto'} className="close-column">
          <Button onClick={hide} type="text" shape="circle" block>
            <Icon type="x" color="primary" width={14} />
          </Button>
        </Col>
      </Row>
      {auth.assumingUserId && !md && (
        <Row align="middle" className="assume-row">
          <Col flex="auto">
            <UnassumeButton />
          </Col>
        </Row>
      )}
      <MlsNumberSelectionModal
        users={assumeUser}
        visible={mlsModalOpen}
        onModalClose={() => setMlsModalOpen(false)}
      />
    </>
  );
};

const UnassumeButton: React.FC = () => {
  const onUnassumeClick = () => {
    localStorage.removeItem(DASHBOARD_LISTING_STORAGE_KEYS.status);
    localStorage.removeItem(DASHBOARD_LISTING_STORAGE_KEYS.availability);
    window.location.href = '/unassume';
  };

  return (
    <Button
      type="primary"
      danger
      className="unassume-button"
      onClick={onUnassumeClick}
    >
      <Text strong>WARNING - ASSUMED USER (CLICK TO UNASSUME)</Text>
    </Button>
  );
};

const StyledDropdownText = styled(Text)`
  cursor: pointer;
`;

const StyledArrowIcon = styled(Icon)<{ $overlayVisible: boolean }>`
  margin: 0 5px;
  && {
    ${({ $overlayVisible }) =>
      `transform: scaleY(${$overlayVisible ? -1 : 1});`}
    transition: all 1s;
  }
`;

const StyledMenuText = styled(Text)`
  color: #595959;
`;

const StyledMenuItem = styled(MenuItem)<{ $isSelected: boolean }>`
  &&& {
    border-radius: 0px;
  }
  ${({ $isSelected }) => {
    if ($isSelected)
      return `
      background-color: #E4EEFE;
      font-weight: bold;
      &&& {
        &:hover {
          background-color: #E4EEFE;
        }
      }
    `;
  }}
`;

const StyledMenu = styled(Menu)`
  overflow: hidden;
  width: 135px;
  &&& {
    padding: 0;
  }
`;

export default BBAssumeNavbar;
